import React, {useMemo, useState} from "react"
import {PieChart, Pie, Cell, Tooltip, ResponsiveContainer} from "recharts"
import {useSirenBreakdown, BreakdownCategoryStats, AlertState} from "../hooks/useSirenAlertWS"
import {Card, PieTooltip, DeliveryAttemptsModal, CycleTabSelector, SirenAlert} from "./AlertStatusShared"

const CATEGORY_COLORS: Record<string, string> = {
    sms:   "#4e79a7",
    email: "#f28e2b",
    call:  "#59a14f",
    app:   "#76b7b2",
}

const CATEGORY_LABELS: Record<string, string> = {
    sms:   "SMS",
    email: "Email",
    call:  "Phone Call",
    app:   "App",
}

export function DeliveryBreakdownCard({
    alertID,
    alert,
    state,
    selectedCycle,
    onCycleChange,
}: {
    alertID: string
    alert: SirenAlert | null
    state: AlertState | null
    selectedCycle: number | "all"
    onCycleChange: (c: number | "all") => void
}) {
    const {data} = useSirenBreakdown(alertID)

    const allCycles = useMemo(() => {
        if (!data) return []
        return [...new Set(data.categories.map(c => c.cycle))].sort((a, b) => a - b)
    }, [data])

    // Aggregate for the selected cycle
    const aggregated = useMemo(() => {
        if (!data) return []
        const rows: BreakdownCategoryStats[] = selectedCycle === "all"
            ? data.categories
            : data.categories.filter(c => c.cycle === selectedCycle)

        const byCategory: Record<string, {sent: number; pendingCallback: number; failed: number; skipped: number}> = {}
        for (const r of rows) {
            const existing = byCategory[r.category] ?? {sent: 0, pendingCallback: 0, failed: 0, skipped: 0}
            byCategory[r.category] = {
                sent:            existing.sent + r.sent,
                pendingCallback: existing.pendingCallback + r.pendingCallback,
                failed:          existing.failed + r.failed,
                skipped:         existing.skipped + r.skipped,
            }
        }
        return Object.entries(byCategory).map(([category, stats]) => ({category, ...stats}))
    }, [data, selectedCycle])

    // Filter to only configured delivery method categories
    const resolvedMethods = alert?.useDefaultDeliveryMethods
        ? (state?.alertConfig?.deliveryMethods ?? [])
        : (alert?.deliveryMethods ?? [])

    const categoryData = useMemo(() => {
        const order = ["sms", "email", "call", "app"]
        return order
            .filter(cat => {
                if (cat === "sms")   return resolvedMethods.some(m => m.startsWith("sms_"))
                if (cat === "email") return resolvedMethods.some(m => m === "email_work" || m === "email_personal")
                if (cat === "call")  return resolvedMethods.some(m => m.startsWith("call_"))
                if (cat === "app")   return resolvedMethods.some(m => m.startsWith("app_"))
                return false
            })
            .map(cat => {
                const stats = aggregated.find(a => a.category === cat) ?? {sent: 0, pendingCallback: 0, failed: 0, skipped: 0}
                return {
                    category: cat,
                    name: CATEGORY_LABELS[cat] ?? cat,
                    color: CATEGORY_COLORS[cat] ?? "#999",
                    value: stats.sent + stats.pendingCallback,
                    ...stats,
                }
            })
    }, [aggregated, resolvedMethods])

    const total = categoryData.reduce((s, c) => s + c.value, 0)

    const [deliveryModal, setDeliveryModal] = useState<{label: string; category: string} | null>(null)

    return (
        <>
            <Card
                title="Delivery Breakdown"
                headerActions={
                    <CycleTabSelector cycles={allCycles} selected={selectedCycle} onChange={onCycleChange} />
                }
            >
                <div className="flex flex-col gap-2">
                    {categoryData.length > 0 && (
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={categoryData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="55%"
                                    outerRadius="80%"
                                    dataKey="value"
                                    nameKey="name"
                                    strokeWidth={0}
                                    isAnimationActive={false}
                                >
                                    {categoryData.map((entry, i) => (
                                        <Cell
                                            key={i}
                                            fill={entry.color}
                                            cursor="pointer"
                                            onClick={() => setDeliveryModal({label: entry.name, category: entry.category})}
                                            className="hover:opacity-80 transition-opacity"
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<PieTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                    {categoryData.map(entry => {
                        const pct = total > 0 ? (entry.value / total) * 100 : 0
                        return (
                            <button
                                key={entry.category}
                                className="flex flex-col gap-1 text-left hover:opacity-80 transition-opacity cursor-pointer"
                                onClick={() => setDeliveryModal({label: entry.name, category: entry.category})}
                            >
                                <div className="flex justify-between text-xs w-full">
                                    <span className="font-medium underline-offset-2 hover:underline" style={{color: entry.color}}>{entry.name}</span>
                                    <span className="text-grey">{entry.value} ({pct.toFixed(0)}%)</span>
                                </div>
                                <div className="bg-light-grey rounded-full h-3 overflow-hidden w-full">
                                    <div
                                        className="h-3 rounded-full transition-all duration-500"
                                        style={{width: `${pct}%`, background: entry.color}}
                                    />
                                </div>
                            </button>
                        )
                    })}
                    {categoryData.length === 0 && (
                        <p className="text-sm text-grey text-center py-4">No items sent yet</p>
                    )}
                </div>
            </Card>

            {deliveryModal && (
                <DeliveryAttemptsModal
                    alertID={alertID}
                    label={deliveryModal.label}
                    category={deliveryModal.category}
                    onClose={() => setDeliveryModal(null)}
                />
            )}
        </>
    )
}
